import { GetServerSideProps, NextPage } from "next"
import Head from "next/head"
import { isEmpty } from "lodash"
import { useContext } from "react"
import { AppContext } from "AppContext"
import { Page } from "@styled/dashboard"
import Header from "@modules/Common/components/Header"
import { Row, Container } from "react-bootstrap"
import { initializeApollo } from "apollo/client"
import PublicActivityView from "@modules/Activity/components/PublicActivityView"
import ActivityDetail from "@modules/Activity/components/ActivityDetail"
import { DEFAULT_META_IMAGE_URL } from "@utils/constant"
import { PublicActivityDocument, PublicActivityQuery, PublicActivityQueryVariables } from "@generated/graphql"

interface IActivityPage {
  error?: string
  slug: string
  isPublic: boolean
  activity?: PublicActivityQuery["publicActivity"]
}

const ActivityPage: NextPage<IActivityPage> = ({ slug, isPublic, activity }) => {
  const { state } = useContext(AppContext)

  const title = `${activity?.metaTagData?.title || activity?.title || ""} | MySkillShaala`
  const description = activity?.metaTagData?.description || activity?.shortDescription || ""
  const image = activity?.metaTagData?.image || activity?.image || DEFAULT_META_IMAGE_URL

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/${activity?.skill?.slug}/${activity?.slug}`} />
        <meta property="og:site_name" content={process.env.NEXT_PUBLIC_SITE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/${activity?.skill?.slug}/${activity?.slug}`} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
        {activity?.isIndexable !== true && <meta name="robots" content="noindex,nofollow" />}
        {activity && <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Skills",
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/skills`
            }, {
              "@type": "ListItem",
              "position": 2,
              "name": activity.skill?.name,
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/skills/${activity.skill?.slug}`
            }, {
              "@type": "ListItem",
              "position": 3,
              "name": activity.title,
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/skills/${activity.skill?.slug}/${activity.slug}`
            }]
          })
        }} />}
        {activity && activity.jsonLD && <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(activity?.jsonLD)
        }} />}
      </Head>
      <Page>
        <Header showCoins={isEmpty(state.user) ? "false" : "true"} />
        <Container className="h-100">
          <Row>
            {isPublic ? <PublicActivityView activity={activity} />
              : <ActivityDetail slug={slug} />}
          </Row>
        </Container>
      </Page>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ params, req: { cookies }, res }) => {
  const slug = Array.isArray(params?.slug) ? params?.slug[1] : params?.slug!!
  const apolloClient = initializeApollo()
  res.setHeader("Cache-Control", "s-maxage=3600, stale-while-revalidate=59")
  const { data, error } = await apolloClient.query<PublicActivityQuery, PublicActivityQueryVariables>({
    query: PublicActivityDocument,
    variables: {
      where: {
        slug: slug
      }
    }
  })
  return {
    props: {
      error: error?.message || null,
      isPublic: !cookies["idToken"],
      slug: slug,
      activity: data.publicActivity,
      initialApolloState: apolloClient.cache.extract(),
    }
  }
}

export default ActivityPage
